import { useQuery } from '@tanstack/react-query';
import { endpoints } from '@rossum/api-client';
import { api } from '../../../lib/apiClient';

export type ValidationError = {
  key: string;
  message: string;
};

const extractErrors = (
  obj: unknown,
  path: string,
  errors: ValidationError[]
): void => {
  if (!obj || typeof obj !== 'object') {
    return;
  }

  if (Array.isArray(obj)) {
    obj.forEach((item, index) => {
      extractErrors(item, `${path}[${index}]`, errors);
    });
    return;
  }

  Object.entries(obj).forEach(([key, value]) => {
    const currentPath = path ? `${path}.${key}` : key;

    if (Array.isArray(value) && value.every(v => typeof v === 'string')) {
      value.forEach(message => {
        errors.push({
          key: currentPath,
          message: String(message),
        });
      });
    } else {
      extractErrors(value, currentPath, errors);
    }
  });
};

const transformValidationResponse = (response: unknown): ValidationError[] => {
  const errors: ValidationError[] = [];

  if (response && typeof response === 'object' && 'content' in response) {
    extractErrors(
      (response as { content: unknown }).content,
      'content',
      errors
    );
  } else {
    extractErrors(response, '', errors);
  }

  return errors;
};

export const useValidateRawSchema = (content: unknown, enabled: boolean) => {
  return useQuery({
    queryKey: ['validate-raw-schema', JSON.stringify(content)],
    queryFn: async () => {
      const response = await api.request(
        endpoints.schemas.validateRaw({ content })
      );
      return transformValidationResponse(response);
    },
    enabled,
    staleTime: Infinity,
  });
};
